import React from 'react';
import { SectionHeader, Verdict } from './reportPrimitives';

// A verbatim quote of something the speaker actually said (opening / closing line).
function Quote({ children }) {
  return (
    <p
      className="text-sm italic mt-1 mb-1 pl-3"
      style={{ color: 'var(--text-secondary)', borderLeft: '2px solid var(--border-accent)' }}
    >
      "{children}"
    </p>
  );
}

// Fallback maps for speeches analysed before the explicit `status` field existed.
// story-driven is 'warn' (not 'good'): for a product walkthrough, story crowding
// out the product is a caution, not a win.
const CLARITY = { clear: 'good', muddled: 'warn', absent: 'bad' };
const STRENGTH = { strong: 'good', weak: 'warn' };
const BALANCE = { 'story-driven': 'warn', balanced: 'good', 'all-abstract': 'bad' };
const STRUCT = { clear: 'good', loose: 'warn', 'hard-to-follow': 'bad' };

// Prefer the model's explicit per-card status (which is written to agree with the
// comment); fall back to the categorical map for older stored analyses.
const flag = (map, key, status) => {
  const v = status || map[key];
  return { good: v === 'good', warning: v === 'warn' };
};

export default function ContentReport({ content }) {
  if (!content) return null;
  const { throughline, hook, story, closing, structure, takeaway } = content;

  return (
    <div
      className="rounded-xl p-4 space-y-4"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
    >
      <SectionHeader>Content &amp; Ideas</SectionHeader>

      {/* Throughline — the single core idea */}
      {throughline && (
        <Verdict label="Central idea" {...flag(CLARITY, throughline.clarity, throughline.status)}>
          {throughline.sentence
            ? <>Your talk boils down to: <span style={{ color: 'var(--text-secondary)' }}>"{throughline.sentence}"</span>{throughline.comment ? ` — ${throughline.comment}` : ''}</>
            : (throughline.comment || 'No single clear idea came through. A TEDx talk needs one sentence the audience can repeat.')}
        </Verdict>
      )}

      {/* Hook */}
      {hook && (
        <div>
          <Verdict
            label={`Opening${hook.type && hook.type !== 'generic' ? ` — ${hook.type.replace('_', ' ')}` : ''}`}
            {...flag(STRENGTH, hook.strength, hook.status)}
          >
            {hook.comment}
          </Verdict>
          {hook.opening_line && <Quote>{hook.opening_line}</Quote>}
        </div>
      )}

      {/* Closing */}
      {closing && (
        <div>
          <Verdict
            label={`Closing${closing.has_call_to_action ? ' — with call to action' : ''}`}
            {...flag(STRENGTH, closing.strength, closing.status)}
          >
            {closing.comment}
          </Verdict>
          {closing.closing_line && <Quote>{closing.closing_line}</Quote>}
        </div>
      )}

      {/* Story */}
      {story && (
        <Verdict label="Story vs. abstraction" {...flag(BALANCE, story.balance, story.status)}>
          {story.comment}
        </Verdict>
      )}

      {/* Structure */}
      {structure && (
        <Verdict label="Structure" {...flag(STRUCT, structure.clarity, structure.status)}>
          {structure.comment}
        </Verdict>
      )}

      {/* Takeaway */}
      {takeaway && (
        <div
          className="rounded-lg p-3 mt-1"
          style={{ background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)' }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)' }}>
            What they'll remember
          </span>
          <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>{takeaway}</p>
        </div>
      )}
    </div>
  );
}
